import { sortBy, toLower, uniqBy } from "lodash/fp";

import { STAGE } from "../config/constants";
import {
  CONFERENCING_MEETING_PLATFORMS,
  CONFERENCING_PLATFORMS_BY_ID,
  ConferencingPlatform,
} from "../constants/platforms";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "../constants/providers";
import type {
  ConferencingMeetingPlatform,
  ProductivityProvider,
  Topic,
  TopicChannelProvider,
  TopicChannelPseudoProvider,
  TopicError,
  TopicResponse,
} from "../types";

export const isTopicErrorResponse = (
  response?: TopicResponse,
): response is TopicError => response != null && "error" in response;

export const isMeetingProvider = (
  provider: TopicChannelProvider,
): provider is ConferencingMeetingPlatform =>
  CONFERENCING_MEETING_PLATFORMS.includes(
    provider as ConferencingMeetingPlatform,
  );

export const isUnknownOrAggregateProvider = (
  provider: TopicChannelProvider,
): provider is TopicChannelPseudoProvider =>
  provider === "unknown" || provider === "multi";

const getProviderConfig = (
  provider: TopicChannelProvider,
): ConferencingPlatform | ProductivityProvider | null => {
  if (isUnknownOrAggregateProvider(provider)) return null;

  const config = isMeetingProvider(provider)
    ? CONFERENCING_PLATFORMS_BY_ID[provider]
    : PRODUCTIVITY_PROVIDERS_BY_ID[provider];

  if (typeof config === "undefined") {
    throw new Error(`Unknown provider: ${provider}`);
  }

  return config;
};

export const getProviderDisplayName = (
  provider: TopicChannelProvider,
): string | null => {
  try {
    const config = getProviderConfig(provider);
    return config?.displayNameShort ?? null;
  } catch {
    if (STAGE !== "production") {
      throw new Error(
        `Unknown provider: ${provider}, when fetching display name`,
      );
    }
    return null;
  }
};

export const getProviderLogoSrc = (
  provider: TopicChannelProvider,
): string | null => {
  try {
    const config = getProviderConfig(provider);
    return config?.icon ?? null;
  } catch {
    if (STAGE !== "production") {
      throw new Error(`Unknown provider: ${provider}, when fetching icon`);
    }
    return null;
  }
};

export const getUniqueContributors = (contributors: Topic["contributors"]) => {
  // first, dedupe by any users that have the same user id but may have different
  // display names, then dedupe by display name
  const uniqueContributors = uniqBy(
    "displayName",
    uniqBy("userId", contributors),
  );
  return sortBy(
    (c) => (c.displayName ? toLower(c.displayName) : null),
    uniqueContributors,
  );
};
